import React, { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../../../reducers'
import { Box, Fab, CircularProgress } from '@mui/material'
import { SaveRounded } from '@mui/icons-material'
import AddPageBlock from '../add-page-block'
import PageBlock from '../page-block'
import { useSkillBitePageBlocksForm } from './hooks/useSkillBitePageBlocksForm'
import { FormProvider } from 'react-hook-form'
import { isEqual } from 'lodash'

const PageBody = () => {
  const isWorking = false
  const { skillBiteId } = useParams()

  const selectedPage = useSelector(SkillBiteEditSlice.selectors.selectSelectedPage)
  const canEditStructure = useSelector(SkillBiteEditSlice.selectors.selectCanEditStructure)
  const isPublished = useSelector(SkillBiteEditSlice.selectors.selectIsPublished)
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)
  const selectedPageIdCache = useRef()
  const addBlockButtonRef = useRef()

  const {
    form,
    blocks,
    addBlock,
    deleteBlock,
    resetForm,
    updateBlocks
  } = useSkillBitePageBlocksForm({
    defaultValues: { blocks: selectedPage?.blocks || [] },
    onAddBlockSuccess: () => {
      addBlockButtonRef.current.collapse()
    },
    skillBiteId
  })

  useEffect(() => {
    if (!selectedPage && selectedPageIdCache.current !== undefined) {
      selectedPageIdCache.current = undefined
      resetForm([])
      return
    }

    if (selectedPage && selectedPageIdCache.current !== selectedPage?.id) {
      selectedPageIdCache.current = selectedPage.id
      resetForm(selectedPage.blocks)
    }
  }, [selectedPage, resetForm])

  // watch form changes
  const formWatch = form.watch()
  const hasChanges = React.useMemo(() => {
    if (!selectedPage?.blocks || !formWatch.blocks) return false
    const diff = Object.keys(selectedPage?.blocks).find(key => !isEqual(selectedPage?.blocks[key], formWatch.blocks[key]))
    return !!diff
  }, [formWatch, selectedPage])

  if (!selectedPage) return null
  return (
    <Box mt={2}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(updateBlocks)}>
          {blocks.map((block, index) => {
            return (
              <PageBlock
                control={form.control}
                onDelete={deleteBlock}
                index={index}
                block={block}
                key={block.key}
              />
            )
          })}
          {canEditStructure && (
            <AddPageBlock
              onAddBlock={addBlock}
              ref={addBlockButtonRef}
            />
          )}
          {(blocks?.length > 0 && canEdit) && (
            <Fab
              variant='extended'
              color='primary'
              size='medium'
              type='submit'
              data-test='skill-bite-build-save-button'
              disabled={isWorking || !form.formState.isValid || !hasChanges}
              sx={{ position: 'absolute', bottom: 8, right: 8 }}
            >
              {isWorking ? <CircularProgress size='1em' sx={{ mr: 1 }} /> : <SaveRounded sx={{ mr: 1 }} />}
              {isPublished ? 'Save & Publish' : 'Save'}
            </Fab>
          )}
        </form>
      </FormProvider>
    </Box>
  )
}

export default PageBody
